import React, {
    FunctionComponent,
    memo,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { animated, SpringValue } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'

import { getGestureStyling } from '../utils/GestureHelper'

type Position = {
    x: number
    y: number
}

type PanGestureProps = {
    noExpand?: boolean
    center?: boolean
    width?: string | number
    height?: string | number
    isLayoutProp?: boolean
    position?: Position
    usePanStartPosition?: boolean
    translate: { translateX: SpringValue; translateY: SpringValue }
    startAnimation: {
        startAnimationX: (newValue: number) => void
        startAnimationY: (newValue: number) => void
    }
    onPanStart?: () => void
    onPanActive?: (position: Position) => void
    onPanEnd?: (position: Position) => void
    children: ReactElement
}

type StyleProps = {
    willChange: 'transform'
    width?: string | number
    height?: string | number
    flexGrow: number
    flexShrink: number
    display?: 'flex'
    justifyContent?: 'center'
    alignItems?: 'center'
}

const PanGesture: FunctionComponent<PanGestureProps> = ({
    noExpand,
    center,
    width,
    height,
    position,
    usePanStartPosition,
    startAnimation,
    onPanActive,
    onPanStart,
    onPanEnd,
    children,
}) => {
    const mouseDown = useRef(false)
    const animatedDivRef = React.useRef<HTMLDivElement>(document.createElement('div'))
    const [offsetLeft, setOffsetLeft] = useState(0)

    const style = useMemo<StyleProps>(() => {
        return {
            willChange: 'transform',
            ...getGestureStyling(width, height, noExpand, center),
        }
    }, [noExpand, center, width, height])

    useEffect(() => {
        if (position) {
            startAnimation.startAnimationX(position.x)
            startAnimation.startAnimationY(position.y)
        }
    }, [position, startAnimation])

    useEffect(() => {
        setOffsetLeft(animatedDivRef.current.getBoundingClientRect().left)
    }, [])

    const bind = useDrag(({ down, offset: [oX, oY], values: [x, y] }) => {
        const xDest = usePanStartPosition ? x - offsetLeft : oX
        const yDest = usePanStartPosition ? y - offsetLeft : oY
        if (down !== mouseDown.current) {
            const position = { x, y }
            mouseDown.current = down
            if (down) {
                onPanStart && onPanStart()
            } else {
                onPanActive && onPanActive(position)
                onPanEnd && onPanEnd(position)
            }
        }
        startAnimation.startAnimationX(xDest)
        startAnimation.startAnimationY(yDest)
    })

    return (
        <animated.div ref={animatedDivRef} style={style} {...bind()}>
            {children}
        </animated.div>
    )
}

export default memo(PanGesture)
